//@flow
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import _ from 'lodash';
import type { BusinessRecord } from '../../types/business';
import { ModalTypes } from '../../context/modalContext';
import { useAuth, useModal } from '../../hooks';
import { FavoriteIcon } from '../icons';
import FixedRatioImage from '../base/FixedRatioImage';
import styles from './search-result.module.sass';

type Props = {
  rootRef: HTMLDivElement;
  item: BusinessRecord;
  lastBusinessRef?: any;
};

const BusinessThumb = ({ rootRef, item, lastBusinessRef }: Props) => {
  const { user } = useAuth();
  const { setModalType, setFavoriteBusiness } = useModal();
  const containerRef = useRef<HTMLDivElement>();
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        setIntersecting(entry.intersectionRatio > 0.05);
      }
    },
    {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      rootMargin: '0px',
      root: rootRef,
    }
  );

  useEffect(() => {
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const isSelected =
    user &&
    user.data.favorites &&
    _.some(user.data.favorites, (boards) => {
      return _.some(boards.businesses, (businessId) => {
        return businessId === item.id;
      });
    });

  const handleFavoriteClicked = (e: React.MouseEvent<HTMLElement>) => {
    if (isSelected) return;
    e.preventDefault();
    setFavoriteBusiness(item);
    setModalType(ModalTypes.FAVORITE);
  };

  const { headerImage, about, name, slug } = item.data;

  //TODO: REMOVE THIS BEFORE LIVE
  // const getRandomImage = () => {
  //   const number = Math.floor(Math.random() * 7 + 1);
  //   return `/dummy/business/bus${number}_placeholder.png`;
  // };
  // const tempImage = React.useMemo(() => {
  //   return user && user.business && user.business.id === item.id
  //     ? headerImage
  //     : getRandomImage();
  // }, []);

  let thumbImage = headerImage;
  if (!headerImage || !headerImage.length || !headerImage.includes('/uploads/')) {
    const imageOne =
      about && about.moodImages ? _.find(about.moodImages, { position: 1 }) : null;
    const imageTwo =
      about && about.moodImages ? _.find(about.moodImages, { position: 2 }) : null;
    const imageThree =
      about && about.moodImages ? _.find(about.moodImages, { position: 3 }) : null;

    const hasImageOne =
      imageOne &&
      imageOne.image &&
      imageOne.image.length > 0 &&
      imageOne.image.includes('/uploads/');

    const hasImageTwo =
      imageTwo &&
      imageTwo.image &&
      imageTwo.image.length > 0 &&
      imageTwo.image.includes('/uploads/');

    const hasImageThree =
      imageThree &&
      imageThree.image &&
      imageThree.image.length > 0 &&
      imageThree.image.includes('/uploads/');

    if (hasImageTwo) {
      thumbImage = imageTwo.image;
    } else if (hasImageOne) {
      thumbImage = imageOne.image;
    } else if (hasImageThree) {
      thumbImage = imageThree.image;
    }
  }

  return (
    <Link href={`/business/${slug}`}>
      <a
        ref={lastBusinessRef && lastBusinessRef}
        className={`${styles.thumb} ${!isIntersecting ? styles.hide : ''}`}
      >
        <FixedRatioImage image={thumbImage} ratio="1x1" interactive>
          <div ref={containerRef} className={styles.icon}>
            <FavoriteIcon selected={isSelected} onClick={handleFavoriteClicked} />
          </div>
          {item.data.specs && item.data.specs.ownership.indigenousOwned && (
            <div className={styles.indigenous}>
              <img
                className={styles.background}
                src="/images/gift-guide-blob.svg"
                alt=""
              />
              <div className={styles.label}>
                <p>IB</p>
              </div>
            </div>
          )}
        </FixedRatioImage>
        <p>{name}</p>
      </a>
    </Link>
  );
};

export default BusinessThumb;
